import { callOrReturn, getExtensionField, mergeAttributes, Node, type NodeViewRendererProps, type ParentConfig } from '@tiptap/core';
import { type DOMOutputSpec, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { convertToPt, convertToPx } from '../extension-word/utils';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { createTable, deleteTableWhenAllCellsSelected } from './utils';
import {
    deleteColumn,
    tableEditing,
    deleteTable,
    addRowBefore,
    addRowAfter,
    deleteRow,
    mergeCells,
    splitCell,
    goToNextCell,
    setCellAttr,
    CellSelection,
    addColumnBefore,
    addColumnAfter,
} from './pm-table-plugin';
import { tableResizePlugin } from './pm-table-resize-plugin';
import { createColGroup } from './utils/createColGroup';
import { WordTableView } from './WordTableView';

export interface TableOptions {
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        table: {
            insertTable: (options?: { rows?: number; cols?: number; withHeaderRow?: boolean }) => ReturnType;
            addColumnBefore: () => ReturnType;
            addColumnAfter: () => ReturnType;
            deleteColumn: () => ReturnType;
            addRowBefore: () => ReturnType;
            addRowAfter: () => ReturnType;
            deleteRow: () => ReturnType;
            deleteTable: () => ReturnType;
            mergeCells: () => ReturnType;
            splitCell: () => ReturnType;
            toggleHeaderColumn: () => ReturnType;
            toggleHeaderRow: () => ReturnType;
            toggleHeaderCell: () => ReturnType;
            mergeOrSplit: () => ReturnType;
            setCellAttribute: (name: string, value: any) => ReturnType;
            goToNextCell: () => ReturnType;
            goToPreviousCell: () => ReturnType;
            fixTables: () => ReturnType;
            setCellSelection: (position: { anchorCell: number; headCell?: number }) => ReturnType;
        };
    }

    interface NodeConfig<Options, Storage> {
        /**
         * Table Role
         */
        tableRole?: string | ((this: { name: string; options: Options; storage: Storage; parent: ParentConfig<NodeConfig<Options>>['tableRole'] }) => string);
    }
}

export const Table = Node.create<TableOptions>({
    name: 'table',

    addOptions() {
        return {
            HTMLAttributes: {
                class: 'relative border-collapse break-all table-fixed m-0 [--word-table-border-transparent-color:transparent] hover:[--word-table-border-transparent-color:#ff0000]',
            },
        };
    },

    content: 'tableRow+',
    tableRole: 'wordTable',
    isolating: true,
    group: 'block',

    addAttributes() {
        /* attrs: {
            "width": null,  // ooxml기준 <w:tblW w:w="0" w:type="auto"/> 값은 {value: 0, type: 'AUTO'}, {value: 0, type: 'POINTS'}, {value: 0, type: 'PERCENT'}
            "indent": "0.0pt",
            "positionProperties": {
                "positionX": "0.0pt",
                "positionY": "0.0pt",
                "horzAnchor": "column",
                "vertAnchor": "margin",
                "rightFromText": "0.0pt",
                "leftFromText": "0.0pt",
                "alignment": null,  // 실제 ooxml기준 <w:jc w:val="center"/> 값은 null, left, center, right
            },
        } */
        return {
            width: {
                default: { value: 0, type: 'AUTO' },
                parseHTML: element => {
                    return JSON.parse(element.dataset.width || 'null') || { value: 0, type: 'AUTO' };
                },
                renderHTML: attributes => {
                    const { value, type } = attributes.width ?? {};
                    const isAuto = type === 'AUTO';
                    const isPoint = type === 'POINTS';
                    const isZeroPoint = isPoint && value === 0;
                    const isPercent = type === 'PERCENT';
                    return {
                        ...(isAuto || isZeroPoint ? { style: `max-width: 100%;` } : {}),
                        ...(isPercent ? { style: `min-width: ${value}%; max-width: ${value}%;` } : {}),
                        ...(isPoint && !isZeroPoint ? { style: `min-width: ${value}pt; max-width: ${value}pt;` } : {}),
                        ...(attributes.width ? { 'data-width': JSON.stringify(attributes.width) } : {}),
                    };
                },
            },
            // allowAutoFit 을 구현하기 어려운 구조라 UX적으로 반영하지 않음
            allowAutoFit: {
                default: true,
                parseHTML: element => {
                    return element.dataset.allowAutoFit !== 'false';
                },
                renderHTML: attributes => {
                    return {
                        'data-allow-auto-fit': attributes.allowAutoFit,
                    };
                },
            },
            indent: {
                default: null,
                parseHTML: element => {
                    return element.dataset.indent || null;
                },
                renderHTML: attributes => {
                    return {
                        // style: attributes.indent ? `margin-left: ${attributes.indent}` : '', // positionProperties.leftFromText 와 합산하여 처리
                        'data-indent': attributes.indent,
                    };
                },
            },
            positionProperties: {
                default: null,
                parseHTML: element => {
                    return JSON.parse(element.dataset.positionProperties || 'null');
                },
                renderHTML: attributes => {
                    const { positionX, positionY, horzAnchor, vertAnchor, rightFromText, leftFromText, alignment } = attributes.positionProperties ?? {};
                    const translateX = convertToPt(positionX || '0pt');
                    const translateY = convertToPt(positionY || '0pt');
                    const leftFromTextPx = convertToPx(leftFromText || '0pt');
                    const indentPx = convertToPx(attributes.indent || '0pt');
                    const marginLeft = leftFromTextPx + indentPx;
                    const isAbsolute = translateX !== 0 || translateY !== 0;
                    const isLeft = alignment === 'left' || alignment === null;
                    const isCenter = alignment === 'center';
                    const isRight = alignment === 'right';
                    return {
                        style: [
                            isAbsolute ? `position: absolute; transform: translate(${translateX - leftFromTextPx - indentPx}px, ${translateY}pt);` : '',
                            isLeft && !isAbsolute && marginLeft > 0 ? `margin-left: ${marginLeft}px;` : '',
                            isCenter ? `text-align: center; margin-left: auto; margin-right: auto;` : '',
                            isRight ? `margin-left: auto; margin-right: 0;` : '',
                            isRight && convertToPx(rightFromText || '0pt') ? `margin-right: ${rightFromText};` : '',
                        ].join(''),
                        ...(attributes.positionProperties ? { 'data-position-properties': JSON.stringify(attributes.positionProperties) } : {}),
                    };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'table' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        const { colgroupWithStyle } = createColGroup(node, 0);
        const table: DOMOutputSpec = ['table', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {}), colgroupWithStyle, ['tbody', 0]];

        return table;
    },

    addNodeView() {
        return (props: NodeViewRendererProps) => {
            return new WordTableView({ ...props, HTMLAttributes: mergeAttributes(this.options.HTMLAttributes, props.HTMLAttributes) });
        };
    },

    extendNodeSchema(extension) {
        const context = {
            name: extension.name,
            options: extension.options,
            storage: extension.storage,
        };

        return {
            tableRole: callOrReturn(getExtensionField(extension, 'tableRole', context)),
        };
    },

    addCommands() {
        return {
            insertTable:
                ({ rows = 3, cols = 3 } = {}) =>
                ({ tr, dispatch, editor }) => {
                    const node = createTable(editor, rows, cols);

                    if (dispatch) {
                        const offset = tr.selection.anchor + 1;

                        tr.replaceSelectionWith(node)
                            .scrollIntoView()
                            .setSelection(TextSelection.near(tr.doc.resolve(offset)));
                    }

                    return true;
                },
            addColumnBefore:
                () =>
                ({ state, dispatch }) => {
                    return addColumnBefore(state, dispatch);
                },
            addColumnAfter:
                () =>
                ({ state, dispatch }) => {
                    return addColumnAfter(state, dispatch);
                },
            deleteColumn:
                () =>
                ({ state, dispatch }) => {
                    return deleteColumn(state, dispatch);
                },
            addRowBefore:
                () =>
                ({ state, dispatch }) => {
                    return addRowBefore(state, dispatch);
                },
            addRowAfter:
                () =>
                ({ state, dispatch }) => {
                    return addRowAfter(state, dispatch);
                },
            deleteRow:
                () =>
                ({ state, dispatch }) => {
                    return deleteRow(state, dispatch);
                },
            deleteTable:
                () =>
                ({ state, dispatch }) => {
                    return deleteTable(state, dispatch);
                },
            mergeCells:
                () =>
                ({ state, dispatch }) => {
                    return mergeCells(state, dispatch);
                },
            splitCell:
                () =>
                ({ state, dispatch }) => {
                    return splitCell(state, dispatch);
                },
            toggleHeaderColumn: () => () => {
                // WordTable에서는 헤더를 가지지 않음
                return false;
            },
            toggleHeaderRow: () => () => {
                // WordTable에서는 헤더를 가지지 않음
                return false;
            },
            toggleHeaderCell: () => () => {
                // WordTable에서는 헤더를 가지지 않음
                return false;
            },
            mergeOrSplit:
                () =>
                ({ state, dispatch }) => {
                    if (mergeCells(state, dispatch)) {
                        return true;
                    }

                    return splitCell(state, dispatch);
                },
            setCellAttribute:
                (name, value) =>
                ({ state, dispatch }) => {
                    return setCellAttr(name, value)(state, dispatch);
                },
            goToNextCell:
                () =>
                ({ state, dispatch }) => {
                    return goToNextCell(1)(state, dispatch);
                },
            goToPreviousCell:
                () =>
                ({ state, dispatch }) => {
                    return goToNextCell(-1)(state, dispatch);
                },
            fixTables: () => () => {
                // 원래 비정상 테이블이 생길때 고치는 기능이지만 WordTable에서는 직사각형 형태의 테이블을 가정하지 않기에
                // 호환되지 않아 사용하지 않음
                return false;
            },
            setCellSelection:
                position =>
                ({ tr, dispatch }) => {
                    if (dispatch) {
                        const selection = CellSelection.create(tr.doc, position.anchorCell, position.headCell);
                        tr.setSelection(selection);
                    }

                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Tab: () => {
                if (this.editor.commands.goToNextCell()) {
                    return true;
                }

                if (!this.editor.can().addRowAfter()) {
                    return false;
                }

                return this.editor.chain().addRowAfter().goToNextCell().run();
            },
            'Shift-Tab': () => this.editor.commands.goToPreviousCell(),
            Backspace: deleteTableWhenAllCellsSelected,
            'Mod-Backspace': deleteTableWhenAllCellsSelected,
            Delete: deleteTableWhenAllCellsSelected,
            'Mod-Delete': deleteTableWhenAllCellsSelected,
        };
    },

    addProseMirrorPlugins() {
        return [
            tableEditing(),
            tableResizePlugin(this.editor),
            // section을 기준으로 테이블의 위치 상대좌표를 조정하여 화면에 표시하기 위한 플러그인
            new Plugin({
                key: new PluginKey('tablePosition'),
                state: {
                    init(_, { doc }) {
                        return DecorationSet.empty;
                    },
                    apply(tr, oldDecorations) {
                        // 문서가 변경되지 않았다면 이전 데코레이션을 재사용
                        if (!tr.docChanged) {
                            return oldDecorations;
                        }

                        const decorations: Decoration[] = [];
                        let currentSectionNode: ProseMirrorNode | null = null;

                        // 문서 전체를 순회하면서 section과 table을 찾음
                        tr.doc.descendants((node, pos) => {
                            if (node.type.name === 'section') {
                                currentSectionNode = node;
                            } else if (node.type.spec.tableRole === 'wordTable' && currentSectionNode) {
                                const { positionProperties } = node.attrs;
                                if (!positionProperties) return;

                                const { positionX, positionY, horzAnchor } = positionProperties;
                                const isAbsolute = convertToPt(positionX || '0pt') !== 0 || convertToPt(positionY || '0pt') !== 0;

                                if (isAbsolute) {
                                    if (horzAnchor === 'page') {
                                        const pageSetup = currentSectionNode.attrs.pageSetup;
                                        const leftMargin = pageSetup?.leftMargin || '4pt';
                                        const translateX = positionX ? `calc(${positionX} - ${leftMargin})` : `0`;
                                        const translateY = positionY || '0';

                                        decorations.push(
                                            Decoration.node(
                                                pos,
                                                pos + node.nodeSize,
                                                {
                                                    style: ['position: absolute;', 'transform-box: view-box;', `transform: translate(${translateX}, ${translateY});`].join(' '),
                                                },
                                                { side: 0 },
                                            ),
                                        );
                                    } else {
                                        const translateX = positionX || '0';
                                        const translateY = positionY || '0';
                                        decorations.push(
                                            Decoration.node(
                                                pos,
                                                pos + node.nodeSize,
                                                {
                                                    style: ['position: absolute;', 'transform-box: view-box;', `transform: translate(${translateX}, ${translateY});`].join(' '),
                                                },
                                                { side: 0 },
                                            ),
                                        );
                                    }
                                }
                            }
                        });

                        return DecorationSet.create(tr.doc, decorations);
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },
});
